//
// Copyright 2023 DXOS.org
//

import { Filter, Obj, type Query, Type } from '@dxos/echo';
import { EncodedReference as EncodedRef, type EncodedReference } from '@dxos/echo-protocol';
import { invariant } from '@dxos/invariant';
import { DXN, EID, type URI } from '@dxos/keys';
import { isNonNullable } from '@dxos/util';

import { type EchoDatabase } from './proxy-db';
import type { SerializedSpace } from './serialized-space';

const MAX_LOAD_OBJECT_CHUNK_SIZE = 30;

export type ImportOptions = {
  /**
   * Called for each object before importing.
   * @returns true to import the object, false to skip.
   */
  onObject?: (object: Obj.JSON) => Promise<boolean>;
};

// TODO(burdon): Schema not present when reloaded from persistent store.
// TODO(burdon): Option to decode JSON/protobuf.
// TODO(burdon): Sort JSON keys (npm canonical serialize util).
export class Serializer {
  static version = 1;

  async export(database: EchoDatabase, query?: Query.Any): Promise<SerializedSpace> {
    const loadedObjects: Array<Obj.Any | undefined> = [];

    if (query) {
      const objects = await database.query(query).run();
      loadedObjects.push(...objects);
    } else {
      const ids = database.getAllObjectIds();
      for (const chunk of chunkArray(ids, MAX_LOAD_OBJECT_CHUNK_SIZE)) {
        const objects = await database.query(Filter.id(...chunk)).run({ timeout: 60_000 });
        loadedObjects.push(...objects);
      }
    }

    const data = {
      version: Serializer.version,
      timestamp: new Date().toISOString(),
      objects: loadedObjects.filter(isNonNullable).map((object) => {
        return this.exportObject(object as any);
      }),
    };

    return data;
  }

  async import(database: EchoDatabase, data: SerializedSpace, opts?: ImportOptions): Promise<void> {
    invariant(data.version === Serializer.version, `Invalid version: ${data.version}`);

    const { objects } = data;
    const deferred: Obj.JSON[] = [];
    for (const object of objects) {
      const shouldImport = opts?.onObject ? await opts.onObject(object) : true;
      if (!shouldImport) {
        continue;
      }

      if (object['@relationSource'] || object['@relationTarget']) {
        deferred.push(object);
      } else {
        await this._importObject(database, object);
      }
    }

    // Flush so source/target objects are queryable before importing relations.
    await database.flush();
    for (const object of deferred) {
      await this._importObject(database, object);
    }

    await database.flush();
  }

  exportObject(object: Obj.Any): Obj.JSON {
    return Obj.toJSON(object);
  }

  private async _importObject(database: EchoDatabase, data: Obj.JSON): Promise<void> {
    const obj = await Obj.fromJSON(data, {
      refResolver: database.graph.createRefResolver({ context: { space: database.spaceId } }),
    });
    // Type entities must be persisted via `addType` (clones/forks + conflict check); `add` rejects them.
    if (Type.isType(obj)) {
      await database.addType(obj);
    } else {
      database.add(obj);
    }
  }
}

export const decodeDXNFromJSON = (encoded?: EncodedReference | string): URI.URI | undefined => {
  if (typeof encoded === 'object' && encoded !== null && '/' in encoded) {
    return EncodedRef.toURI(encoded);
  } else if (typeof encoded === 'string') {
    if (DXN.isDXN(encoded) || EID.isEID(encoded)) {
      return encoded;
    }
    // Treat plain strings as type names.
    return DXN.make(encoded);
  }
};

const chunkArray = <T>(arr: T[], chunkSize: number): T[][] => {
  if (arr.length === 0 || chunkSize < 1) {
    return [];
  }

  let index = 0;
  let resIndex = 0;
  const result = new Array(Math.ceil(arr.length / chunkSize));
  while (index < arr.length) {
    result[resIndex++] = arr.slice(index, (index += chunkSize));
  }

  return result;
};
